import React, { useEffect, useState } from "react";
// import DatePicker from "../../ui/forms/DatePicker";
import ReactTooltip from "react-tooltip";
import NoRecord from "../../Components/NoRecord";
import ContentLoader from "../../Components/ContentLoader";
import { format, isValid } from "date-fns";
import { getAgentPerformanceSummary } from "../../API/agentApis"; // Assuming this is now getAgentPerformanceSummary
import useAuthStore from "../../store/authStore";
import IncomingCall from "../Components/IncomingCall";
import { showToast } from "../../utils/handleToast";
import { commaFormat } from "../../Functions/formatNumber";
import AgentEarningFilter, { filterDefaultsAgentEarning } from "../../Admin/Components/daily-ops/call-quality/AgentEarningFilter";

// Helper function to format time (e.g., "1h 30m") - kept simple for the new API format
const formatTime = (time) => (time || "0m");

// Component for the vertical divider between table cells
function Divider() {
  return (
    <span
      style={{
        position: "absolute",
        top: "50%",
        right: "0%",
        height: "50%",
        width: "1px",
        backgroundColor: "#ccc",
        display: "block",
        transform: "translateY(-50%)",
      }}
    />
  );
}

// --- NEW COMPONENT: Bonus Card ---
const BonusCard = ({ data = [] }) => {
  // Logic: Calculate days with earnings >= 240 (2hr * 60min * ₹2/min)
  const TARGET_DAYS = 10;
  const EARNING_THRESHOLD = 240;
  const BONUS_AMOUNT = 1500;

  // Filter the daily performance data for qualifying days
  const qualifyingDaysCount = data?.filter(day => {
    // Ensure we are comparing numbers
    const earnings = parseFloat(day.earnings) || 0;
    return earnings >= EARNING_THRESHOLD;
  }).length || 0;

  const progressPercentage = Math.min((qualifyingDaysCount / TARGET_DAYS) * 100, 100);
  const remainingDays = Math.max(TARGET_DAYS - qualifyingDaysCount, 0);
  const isUnlocked = qualifyingDaysCount >= TARGET_DAYS;

  // Gift Icon SVG
  const GiftIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2" style={{color: '#3498db'}}>
      <path d="M20 12V22H4V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M22 7H2V12H22V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 22V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="card shadow-sm mb-4 border-0" style={{ backgroundColor: '#F4F7FC', borderRadius: '12px' }}>
      <div className="card-body p-4">
        <h5 className="card-title d-flex align-items-center mb-4" style={{ color: '#495057', fontWeight: '600' }}>
          <GiftIcon /> Your Monthly Bonuses
        </h5>

        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap">
          <div style={{ minWidth: '150px' }}>
             <h2 className="mb-0 fw-bold text-dark">₹{commaFormat(BONUS_AMOUNT)} <span className="text-muted fs-5 fw-normal">Bonus</span></h2>
          </div>

          <div className="flex-grow-1 mx-md-4 my-2 my-md-0 d-flex align-items-center">
            <div className="progress flex-grow-1" style={{ height: '12px', borderRadius: '10px', backgroundColor: '#dce4ec' }}>
              <div 
                className="progress-bar" 
                role="progressbar" 
                style={{ width: `${progressPercentage}%`, backgroundColor: '#5DADE2', borderRadius: '10px' }} 
                aria-valuenow={progressPercentage} 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
            <span className="ms-3 text-muted" style={{ whiteSpace: 'nowrap' }}>
               2+ Hour Days
            </span>
          </div>

          <div className="text-end" style={{ minWidth: '60px' }}>
             <span className="fs-4 fw-normal text-dark">{qualifyingDaysCount} / {TARGET_DAYS}</span>
          </div>
        </div>

        <div className="p-3 rounded" style={{ backgroundColor: '#E9EEF5' }}>
            <p className="mb-0 text-muted" style={{ fontSize: '0.95rem' }}>
                {isUnlocked 
                  ? <span> Congratulations! You have unlocked the <strong>₹{commaFormat(BONUS_AMOUNT)}</strong> bonus.</span>
                  : <span>You are <strong>{remainingDays} qualifying days</strong> away from earning a ₹{commaFormat(BONUS_AMOUNT)} bonus.</span>
                }
            </p>
        </div>
      </div>
    </div>
  );
};

// Main component: MyEarnings
export default function MyEarnings() {
  const userData = useAuthStore((state) => state.userData);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  // NEW STATE for monthly summary
  const [monthlySummary, setMonthlySummary] = useState(null);

  const [date, setDate] = useState(null);
  const [sort, setSort] = useState("");

  // Date-range filter — no agent picker here (this page only ever shows the
  // logged-in agent's own data), so "agent" stays fixed to their own id.
  const [filter, setFilter] = useState({
    ...filterDefaultsAgentEarning,
    agent: userData?.user_id,
  });

  const clearFilter = () => {
    setData(null);
    setSort("");
    setMonthlySummary(null);
  };

  async function fetchData() {
    setLoading(true);

    const apiParams = { agent_id: userData?.user_id };
    if (filter.start_date && isValid(filter.start_date)) {
      apiParams.start_date = format(filter.start_date, 'yyyy-MM-dd');
    }
    if (filter.end_date && isValid(filter.end_date)) {
      apiParams.end_date = format(filter.end_date, 'yyyy-MM-dd');
    }

    const res = await getAgentPerformanceSummary(apiParams);

    if (res?.status && res?.data) {
      // CRITICAL CHANGE: Extracting daily_performance array and monthly_summary object
      setData(res.data?.daily_performance || []);
      setMonthlySummary(res.data?.monthly_summary || null);

    } else {
      showToast({ message: "Unable to fetch details.", variant: "danger" });
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Tooltip content for Not Eligible Talktime
  const notEligibleTooltipContent = "Blank calls + No disposition. Add disposition & avoid longer blanks to earn more.";

  // Icon for the tooltip (e.g., an 'i' for info)
  const infoIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle', marginLeft: '4px' }}>
      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="currentColor"/>
    </svg>
  );

  // Component to render the Monthly Summary Card
  const MonthlySummaryCard = ({ summary }) => {
      if (!summary) return null;

      // Extract and format data for display
      const talkTimeDisplay = (summary.total_talk_time || '0 mins').replace(' mins', 'm');
      const notEligibleTalkTimeDisplay = (summary.total_not_eligible_talktime || '0 mins').replace(' mins', 'm');
      const earningsDisplay = `₹ ${commaFormat(summary.total_earnings || 0)}`;

      // **FINAL FIX:** Using a table structure with table-layout: fixed to enforce alignment
      return (
          <div className="card shadow-sm p-3 mb-4" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
              {/* Use a table to mimic the column layout */}
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                  <tbody>
                      <tr>
                        
                          <td style={{ minWidth: '120px', padding: '0', textAlign: 'left', border: 'none' }}>
                              <strong style={{ fontSize: "1.25rem" }}>{summary.month_year}</strong>
                          </td>

                          
                          <td className="text-center" style={{ minWidth: '130px', padding: '0 1rem', border: 'none' }}>
                              <span style={{ color: "#6c757d", fontSize: "0.875rem" }}>Total Eligible Talk-Time</span>
                              <br />
                              <strong style={{ fontSize: "1.1rem" }}>{talkTimeDisplay}</strong>
                          </td>

                         
                          <td className="text-center" style={{ minWidth: '160px', padding: '0 1rem', border: 'none' }}>
                              <span
                                  style={{ color: "#6c757d", fontSize: "0.875rem", cursor: 'pointer' }}
                                  data-tip data-for="monthly_not_eligible_tooltip"
                              >
                                  Total Not Eligible Talktime
                                  {infoIcon}
                              </span>
                              <br />
                              <strong style={{ fontSize: "1.1rem" }}>{notEligibleTalkTimeDisplay}</strong>
                              <ReactTooltip effect="solid" place="bottom" className="py-2 px-3" id="monthly_not_eligible_tooltip">
                                  <div style={{ fontWeight: "normal", maxWidth: "250px", textAlign: 'left' }}>
                                      <strong>Not Eligible:</strong> {notEligibleTooltipContent}
                                  </div>
                              </ReactTooltip>
                          </td>

                         
                          <td className="text-center" style={{ minWidth: '100px', padding: '0 0 0 1rem', border: 'none' }}>
                              <span style={{ color: "#6c757d", fontSize: "0.875rem" }}>Total Earnings</span>
                              <br />
                              <strong style={{ fontSize: "1.1rem" }}>{earningsDisplay}</strong>
                          </td>
                      </tr>
                  </tbody>
              </table>
          </div>
      );
  };

  // Determine is_staff from userData (handle both flat and nested structures for safety)
  const is_staff = userData?.is_staff || userData?.user?.is_staff;

  return (
    // REVISED: Remove 'imp-lead-layout' and use a full-width style container
    <div className="pageContent h-100 w-100" style={{ padding: '1rem', boxSizing: 'border-box' }}>

      {/* Date-range filter — same component the admin panel uses, agent picker hidden */}
      <div className="d-flex justify-content-start mb-3">
        <AgentEarningFilter
          prevFilter={filter}
          setPrevFilter={setFilter}
          showAgentFilter={false}
        />
      </div>

      {/* INSERTED BONUS CARD HERE - CONDITIONALLY RENDERED */}
      {!loading && data && !is_staff && <BonusCard data={data} />}

      {/* Monthly Summary Card */}
      {monthlySummary && <MonthlySummaryCard summary={monthlySummary} />}

      {!!data?.length && !loading && (
        // REVISED: Ensure content takes full available height and width
        <div className="imp-leads-content mt-0 h-100" style={{ width: '100%' }}>
          <div className="table-scroll h-100" style={{ overflowY: 'auto' }}>
            {/* REVISED: Adding a table header for better context */}
            <table className="agent-filter-table" style={{ width: '100%', tableLayout: 'fixed' }}>
                <thead>
                    <tr style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, borderBottom: '2px solid #ddd' }}>
                        {/* Headers maintain their minWidth and alignment for consistency */}
                        <th style={{ minWidth: '120px', padding: '1rem 0', textAlign: 'left' }}>Date</th>
                        <th style={{ minWidth: '130px', padding: '1rem 0', textAlign: 'center' }}>Eligible Talk Time</th>
                        <th style={{ minWidth: '160px', padding: '1rem 0', textAlign: 'center' }}>Not Eligible Talktime</th>
                        <th style={{ minWidth: '100px', padding: '1rem 0', textAlign: 'center' }}>Earnings</th>
                    </tr>
                </thead>
              <tbody>
                {data?.map((item, index) => (
                  <tr key={index}>
                    {/* 1. Date Column */}
                    <td className="py-4" style={{ position: "relative", minWidth: '120px', paddingRight: '1rem' }}>
                      <div>
                        <strong style={{ fontSize: "1rem" }}>
                          {!!item?.date ? format(new Date(item?.date), "dd MMM yyyy") : "No Date"}
                        </strong>
                      </div>
                      <Divider />
                    </td>

                    {/* 2. Eligible Talk Time Column */}
                    <td className="py-4 px-4" style={{ position: "relative", minWidth: '130px' }}>
                      <div className="text-center">
                        <span style={{ fontWeight: "500", color: "#000", fontSize: "1rem" }}>{formatTime(item?.talk_time)}</span>
                        <br />
                        <span style={{ fontSize: "0.75rem" }}>Eligible Talk Time</span>
                      </div>
                      <Divider />
                    </td>

                    {/* 3. Not Eligible Talktime Column */}
                    <td className="py-4 px-4" style={{ position: "relative", minWidth: '160px' }}>
                      <div className="text-center">
                        <span style={{ fontWeight: "500", color: "#000", fontSize: "1rem" }}>
                          {formatTime(item?.not_eligible_talktime)}
                        </span>
                        <br />
                        {/* The span was modified to remove data-tip, data-for, and {infoIcon} */}
                        <span
                          style={{ fontSize: "0.75rem" }}
                        >
                          Not Eligible Talktime
                        </span>
                      </div>
                      <Divider />
                    </td>

                    {/* 4. Earnings Column */}
                    <td className="py-4" style={{ position: "relative", minWidth: '100px' }}>
                      <div className="text-center">
                        <strong style={{ fontSize: "1rem" }}>Rs. {commaFormat(parseInt(item?.earnings || 0))}</strong>
                        <br />
                        <span style={{ fontSize: "0.75rem" }}>Earnings</span>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !data?.length && <NoRecord />}
      {loading && <ContentLoader />}
      <IncomingCall />
    </div>
  );
}